import React, { useState } from 'react';
import { Button, Table } from 'antd';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Tag } from '@components/common';
import { COLORS } from '@theme/styles';
import UserActions, { UsersSelectors } from '@redux/UsersRedux';
import { AuthSelectors } from '@redux/AuthRedux';
import { TableHeader, TableFooter } from '@components/table';
import { filterItems, sorter, paginateItems } from '@utils/filters';
import history from '@utils/history';

import './UsersTable.less';
import startCase from 'lodash/startCase';
import camelCase from 'lodash/camelCase';

function renderUserRole(role) {
  switch (role) {
    case 'house_keeper':
      return <Tag content="House Keeper" color={COLORS.cyan} />;

    case 'super_admin':
      return <Tag content="Super Admin" color={COLORS.dark_green} />;

    case 'admin':
      return <Tag content="Admin" color={COLORS.dark_blue} />;

    case 'property_manager':
      return <Tag content="Property Manager" color={COLORS.dark_yellow} />;
  }
}

function Users(props) {
  const {
    users,
    currentUser,
    loading,
    listOfRole,
    listOfNames,
    listOfEmail,
    listOfPhone,
    reloadData
  } = props;

  const dataColumns = [
    {
      title: 'Type',
      dataIndex: 'role',
      render: value => {
        return renderUserRole(value);
      },
      sorter: (a, b) => sorter(a.role, b.role)
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value, row) => (
        <>
          {row.firstName} {row.lastName}
        </>
      ),
      sorter: (a, b) => sorter(a.name, b.name)
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: (a, b) => sorter(a.email, b.email)
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      sorter: (a, b) => sorter(a.phone, b.phone)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'capitalise',
      render: value => (
        <>
          {value === 'active' ? (
            <Tag content="Active" color={COLORS.success} />
          ) : (
            <Tag content="InActive" color={COLORS.warning} />
          )}
        </>
      ),
      sorter: (a, b) => sorter(a.status, b.status)
    },
    {
      title: () => (
        <Button type="primary">
          <Link to="/users/create">Create</Link>
        </Button>
      ),
      key: 'action',
      width: 100,
      render: (value, row) => {
        return (
          <div>
            <Button
              type="primary"
              style={{ marginBottom: 5 }}
              onClick={() => {
                history.push(`/users/${row.userID}`);
              }}
              disabled={!isEditable(row.role)}
            >
              Details
            </Button>
            <Button
              type="primary"
              onClick={() => {
                history.push(`/users/${row.userID}/edit`);
              }}
              disabled={!isEditable(row.role)}
            >
              Edit
            </Button>
          </div>
        );
      }
    }
  ];

  const [filters, updateFilters] = useState({
    role: [],
    name: [],
    email: [],
    phone: [],
    status: ['active']
  });
  const [noOfItemsToDisplay, changeNoOfItemsToDisplay] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const clearFilters = () => {
    updateFilters({
      role: [],
      name: [],
      email: [],
      phone: [],
      status: ['active']
    });
  };
  const handleFiltersUpdate = (filterType, val) => {
    handlePageChange(1);
    updateFilters({
      ...filters,
      [filterType]: [...val]
    });
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };
  const handleChangeNoOfItemsToDisplay = items => {
    changeNoOfItemsToDisplay(items);
    handlePageChange(1);
  };

  const isEditable = role => {
    if (currentUser) {
      let isSuperAdmin = currentUser.role === 'super_admin';
      let isAdmin = currentUser.role === 'admin';
      if (isSuperAdmin) {
        return true;
      } else {
        return isAdmin && role !== 'super_admin';
      }
    }
    return true;
  };

  const header = {
    filters: [
      {
        key: 'role',
        type: 'select',
        label: 'Role',
        data: listOfRole.map(role => ({
          value: role,
          label: startCase(camelCase(role))
        })),
        values: filters.role
      },
      {
        key: 'name',
        type: 'select',
        label: 'Name',
        data: listOfNames,
        values: filters.name
      },
      {
        key: 'email',
        type: 'select',
        label: 'Email',
        data: listOfEmail,
        values: filters.email
      },
      {
        key: 'phone',
        type: 'select',
        label: 'Phone',
        data: listOfPhone,
        values: filters.phone
      },
      {
        key: 'status',
        type: 'boolean',
        label: 'Status',
        defaultValue: 'active',
        values: filters.status
      }
    ],
    updateFilter: handleFiltersUpdate,
    reloadData: reloadData,
    clearFilters: clearFilters
  };

  const filteredItems = filterItems(users, filters);
  const paginatedItems = paginateItems(
    filteredItems,
    currentPage,
    noOfItemsToDisplay
  );
  return (
    <div className="table-view">
      <Table
        columns={dataColumns}
        dataSource={paginatedItems}
        bordered
        pagination={false}
        loading={loading}
        rowKey="userID"
        title={() => <TableHeader {...header} />}
        footer={() => (
          <TableFooter
            changeNoOfItemsToDisplay={handleChangeNoOfItemsToDisplay}
            noOfItemsToDisplay={noOfItemsToDisplay}
            currentItemsDisplayed={filteredItems.length}
            totalItems={users.length}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
            count={filteredItems.length}
          />
        )}
      />
    </div>
  );
}
Users.propTypes = {
  users: PropTypes.array,
  reloadData: PropTypes.func,
  loading: PropTypes.bool,
  currentUser: PropTypes.object,
  listOfRole: PropTypes.array,
  listOfNames: PropTypes.array,
  listOfEmail: PropTypes.array,
  listOfPhone: PropTypes.array
};

const mapStateToProps = state => ({
  users: UsersSelectors.selectUsersList(state),
  loading: UsersSelectors.selectLoading(state),
  currentUser: AuthSelectors.selectCurrentUser(state),
  listOfRole: UsersSelectors.selectList(state).role,
  listOfNames: UsersSelectors.selectList(state).name,
  listOfEmail: UsersSelectors.selectList(state).email,
  listOfPhone: UsersSelectors.selectList(state).phone
});
const mapDispatchToProps = dispatch => ({
  reloadData: () => dispatch(UserActions.refreshUsers())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Users);
